/** A record's phone preview, owned once for the page (the tour plan §4, D6; the catalog
 *  records' O4 / O5): one render state, whichever tab is open and wherever the pane is
 *  drawn — the dock's Preview segment from 1100 px of content, else the drawer the
 *  header's Preview button opens.
 *
 *  - **What a render applies** is built only when a render is asked for, never per
 *    keystroke, and only with changes in the draft (none: the saved record). Viewers
 *    without the kind's manage permission (`canDraft` false) get the saved record. It is
 *    either the record kind's `request` thunk over the draft — the tour's compiled PATCH body
 *    (`{draft}`) — or, for a catalog record, its `changeset` mapping of exactly what Save
 *    would send (O4: what you preview is what you save): the editor's changeset, its choice
 *    answers included (`{changes, options}` for `POST /mobile_preview/` — the re-key answer
 *    and the Custom ID it confirms). Outside edit mode the draft's changes alone.
 *  - **A question first.** With `changeset`, a required question the draft raises and no one
 *    has answered holds the render as it holds Save (`blocked`, the ledger's words — `Answer
 *    “Re-key import matching”`): Render waits and says so, instead of a request the server
 *    would refuse. A question the server raised first — Render pressed within the changeset
 *    preview's debounce, its 400 `required_choice` — holds it the same way, in the same words,
 *    never as a failure: until the draft changes, or the editor's own preview raises the
 *    question (its answer then decides).
 *  - **Fingerprint** = the loaded record's `updatedTs` and the draft's `version`
 *    (`saved:<ts>` while the draft is empty): a render taken of anything else is out of
 *    date — the pane says so; it never renders by itself.
 *  - **An emptied draft** (Discard, undoing every change, a save) takes a render of the
 *    draft off screen: the phone goes back to the saved record — its last render, else
 *    "Not rendered yet" with Render for the saved record — never the discarded draft
 *    labelled "Your unsaved draft".
 *  - **The target**: the day an outline's selection is in (`PreviewTarget`), and the
 *    screen chosen in the pane. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';

import { requiredChoiceOf, type PreviewEntity } from '@/api/mobilePreview';
import { useMobilePreview, type MobilePreviewController, type PreviewApplied } from '@/components/MobilePreview';
import { compileChangeset, draftCount, type DraftState, type RecordChangeset } from '@/features/records/draft/changeSet';
import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { answerFirst } from '@/features/records/editor/choices';
import { RecordEditorContext } from '@/features/records/editor/context';

import { PreviewTarget } from './target';

export interface RecordPreviewValue {
  controller: MobilePreviewController;
  /** The viewer may render the draft (the kind's manage permission). */
  canDraft: boolean;
  /** Why Render cannot apply the draft yet — a required question no one has answered, in the
   *  ledger's words (`Answer “Re-key import matching”`); null when it can. */
  blocked: string | null;
  target: PreviewTarget;
  /** The renderer screen chosen in the pane (undefined: the payload's default). */
  screen: string | undefined;
  setScreen: (screen: string) => void;
  /** The loaded record's `updated_ts` (a fingerprint input — consumers re-render with it). */
  updatedTs: string | null;
}

/** What a render applies, from a draft with changes (the latest one passed is used) — one
 *  of: */
export type RecordPreviewSource =
  /** The draft as the record kind compiles it (the tour's PATCH body, `{draft}`). */
  | { request: (state: DraftState) => PreviewApplied | undefined; changeset?: undefined }
  /** A catalog record (O4): the body Save would send — the editor's changeset with its
   *  choice answers; outside edit mode the draft's changes — as the render takes it
   *  (`{changes, options}`). An unanswered required question holds the render (`blocked`). */
  | { changeset: (body: RecordChangeset) => PreviewApplied; request?: undefined };

export type RecordPreviewProviderProps = RecordPreviewSource & {
  entity: PreviewEntity;
  /** The saved record's id. */
  id: number;
  canDraft: boolean;
  /** The loaded record's `updated_ts`. */
  updatedTs: string | null;
  children: ReactNode;
};

const Ctx = createContext<RecordPreviewValue | null>(null);
/** The target alone: stable for the page, so a writer never re-renders with the render. */
const TargetCtx = createContext<PreviewTarget | null>(null);

export function RecordPreviewProvider({ entity, id, canDraft, updatedTs, children, ...source }: RecordPreviewProviderProps) {
  const { store } = useDraftStable();
  const [target] = useState(() => new PreviewTarget());
  const [screen, setScreen] = useState<string | undefined>(undefined);
  // The editing session around the draft (none outside a record editor): the body Save would
  // send, and the questions it waits for.
  const editor = useContext(RecordEditorContext);
  const raised = editor?.raised;
  const sendsChangeset = source.changeset !== undefined;
  const asked = useMemo(
    () => (sendsChangeset && canDraft && raised ? answerFirst(raised) : null),
    [sendsChangeset, canDraft, raised],
  );
  const version = useSyncExternalStore(store.subscribe, () => store.getState().version);
  // The draft's version each render was asked with: a question the server raised holds only
  // the draft it was raised for.
  const askedWith = useRef<number | null>(null);

  const latest = useRef({ source, updatedTs, body: editor?.body ?? null, blocked: asked });
  latest.current = { source, updatedTs, body: editor?.body ?? null, blocked: asked };

  const applied = useCallback((): PreviewApplied | undefined => {
    const state = store.getState();
    if (draftCount(state) === 0) return undefined;
    const { source: s, body } = latest.current;
    // A catalog record: what Save would send — the editor's body with its answers (null
    // outside edit mode: the draft's changes).
    if (s.changeset) return s.changeset(body ?? compileChangeset(state));
    return s.request(state);
  }, [store]);
  const savedFingerprint = useCallback(() => `saved:${latest.current.updatedTs ?? ''}`, []);
  const fingerprint = useCallback(() => {
    const state = store.getState();
    return draftCount(state) > 0 ? `draft:${latest.current.updatedTs ?? ''}:${state.version}` : savedFingerprint();
  }, [store, savedFingerprint]);

  const preview = useMobilePreview({
    entity,
    id,
    ...(canDraft ? { request: applied, fingerprint } : { fingerprint: savedFingerprint }),
    savedFingerprint,
  });
  // The draft emptied: a render of it is of nothing any more.
  const emptied = useSyncExternalStore(store.subscribe, () => draftCount(store.getState()) === 0);
  const { dropDraft } = preview;
  useEffect(() => {
    if (emptied) dropDraft();
  }, [emptied, dropDraft]);
  // The server's question (400 `required_choice`), worded as the ledger words the editor's:
  // held while the draft is the one it was raised for and the editor has not raised it.
  const serverAsked = useMemo(() => {
    const q = sendsChangeset && canDraft ? requiredChoiceOf(preview.error) : null;
    if (!q || askedWith.current !== version) return null;
    if (q.option && raised?.some((c) => c.option.key === q.option)) return null;
    const label = (q.option && editor?.config.options.find((o) => o.key === q.option)?.label) || q.label;
    return label ? `Answer “${label}”` : null;
  }, [sendsChangeset, canDraft, preview.error, version, raised, editor?.config.options]);
  const blocked = asked ?? serverAsked;
  latest.current.blocked = blocked;

  // A render of the draft waits for its question (a Retry as much as Render).
  const { run: runNow } = preview;
  const run = useCallback(() => {
    if (latest.current.blocked) return;
    askedWith.current = store.getState().version;
    runNow();
  }, [runNow, store]);
  const controller = useMemo<MobilePreviewController>(() => ({ ...preview, run }), [preview, run]);

  const value = useMemo<RecordPreviewValue>(
    () => ({ controller, canDraft, blocked, target, screen, setScreen, updatedTs }),
    [controller, canDraft, blocked, target, screen, updatedTs],
  );
  return (
    <TargetCtx.Provider value={target}>
      <Ctx.Provider value={value}>{children}</Ctx.Provider>
    </TargetCtx.Provider>
  );
}

export function useRecordPreview(): RecordPreviewValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRecordPreview must be used inside <RecordPreviewProvider>');
  return v;
}

/** The target, for writers that must not re-render with the render state (an outline).
 *  Null outside a provider (an outline rendered on its own in a test). */
export function useRecordPreviewTarget(): PreviewTarget | null {
  return useContext(TargetCtx);
}
